import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  fetchAdminBillingSettings,
  updateAdminBillingSettings,
} from "../../api/adminData";


// Field-style class shared across every text/number/select/textarea input.
const inputClass =
  "w-full rounded-[8px] border border-black/10 bg-white px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#64748b] disabled:opacity-60 dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff] dark:placeholder:text-[#6b7a9a]";

const labelClass =
  "mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]";

const sectionTitleClass =
  "sticky top-0 z-10 bg-white dark:bg-[#0c111b] py-2 text-sm font-semibold text-[#0f172a] dark:text-[#eef2ff] border-b border-black/10 dark:border-white/[0.08]";

const sectionClass = "space-y-4 mb-10";
const gridClass = "grid grid-cols-1 md:grid-cols-2 gap-4";

// Fields explicitly excluded from the dirty diff / PATCH payload.
const IMMUTABLE_FIELDS = new Set([
  "id",
  "organization_id",
  "created_at",
  "updated_at",
]);


function setField(setter, key) {
  return (e) => {
    const v = e.target.value;
    setter((f) => ({ ...f, [key]: v }));
  };
}

function setCheckbox(setter, key) {
  return (e) => {
    const v = e.target.checked;
    setter((f) => ({ ...f, [key]: v }));
  };
}


export default function AdminBillingSettingsPage() {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchAdminBillingSettings()
      .then((res) => {
        setForm(res);
        setInitial(res);
      })
      .catch((e) => setError(e?.message || "Laden fehlgeschlagen."))
      .finally(() => setLoading(false));
  }, []);

  const isDirty = useMemo(() => {
    if (!form || !initial) return false;
    return JSON.stringify(form) !== JSON.stringify(initial);
  }, [form, initial]);

  const handleSave = async () => {
    if (!form || !initial) return;
    setSaving(true);
    setError("");

    // Build minimal patch: only fields whose JSON-serialized value changed.
    const patch = {};
    for (const key of Object.keys(form)) {
      if (IMMUTABLE_FIELDS.has(key)) continue;
      if (JSON.stringify(form[key]) !== JSON.stringify(initial[key])) {
        patch[key] = form[key];
      }
    }

    try {
      const res = await updateAdminBillingSettings(patch);
      setForm(res);
      setInitial(res);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-6" data-testid="admin-billing-settings-page">
        <h1 className="text-xl font-semibold text-[#0f172a] dark:text-[#eef2ff] mb-6">
          Rechnungseinstellungen
        </h1>
        <p className="text-[14px] text-[#64748b] dark:text-[#6b7a9a]">Wird geladen …</p>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="px-6 py-6" data-testid="admin-billing-settings-page">
        <h1 className="text-xl font-semibold text-[#0f172a] dark:text-[#eef2ff] mb-6">
          Rechnungseinstellungen
        </h1>
        <p className="mb-3 text-[14px] text-[#f87171]">{error}</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="px-6 py-6" data-testid="admin-billing-settings-page">
        <h1 className="text-xl font-semibold text-[#0f172a] dark:text-[#eef2ff] mb-6">
          Rechnungseinstellungen
        </h1>
        <p className="text-[14px] text-[#64748b] dark:text-[#6b7a9a]">
          Keine Einstellungen vorhanden.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 pb-24" data-testid="admin-billing-settings-page">
      <h1 className="text-xl font-semibold text-[#0f172a] dark:text-[#eef2ff] mb-6">
        Rechnungseinstellungen
      </h1>

      {/* ───────────── Section 1: Identität ───────────── */}
      <section className={sectionClass}>
        <h2 className={sectionTitleClass}>Identität</h2>
        <div className={gridClass}>
          <div>
            <label htmlFor="bs-company_name" className={labelClass}>
              Firmenname *
            </label>
            <input
              id="bs-company_name"
              type="text"
              maxLength={200}
              value={form.company_name || ""}
              onChange={setField(setForm, "company_name")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-address_street" className={labelClass}>
              Strasse
            </label>
            <input
              id="bs-address_street"
              type="text"
              maxLength={200}
              value={form.address_street || ""}
              onChange={setField(setForm, "address_street")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-address_zip" className={labelClass}>
              PLZ
            </label>
            <input
              id="bs-address_zip"
              type="text"
              maxLength={20}
              value={form.address_zip || ""}
              onChange={setField(setForm, "address_zip")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-address_city" className={labelClass}>
              Ort
            </label>
            <input
              id="bs-address_city"
              type="text"
              maxLength={100}
              value={form.address_city || ""}
              onChange={setField(setForm, "address_city")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-address_country" className={labelClass}>
              Land (ISO 2)
            </label>
            <input
              id="bs-address_country"
              type="text"
              maxLength={2}
              value={form.address_country || ""}
              onChange={setField(setForm, "address_country")}
              disabled={saving}
              className={`${inputClass} uppercase`}
              style={{ textTransform: "uppercase" }}
            />
          </div>
          <div>
            <label htmlFor="bs-phone" className={labelClass}>
              Telefon
            </label>
            <input
              id="bs-phone"
              type="text"
              maxLength={50}
              value={form.phone || ""}
              onChange={setField(setForm, "phone")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-email" className={labelClass}>
              E-Mail
            </label>
            <input
              id="bs-email"
              type="email"
              maxLength={200}
              value={form.email || ""}
              onChange={setField(setForm, "email")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-website" className={labelClass}>
              Website
            </label>
            <input
              id="bs-website"
              type="url"
              maxLength={500}
              value={form.website || ""}
              onChange={setField(setForm, "website")}
              disabled={saving}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* ───────────── Section 2: Rechtlich & Steuern ───────────── */}
      <section className={sectionClass}>
        <h2 className={sectionTitleClass}>Rechtlich &amp; Steuern</h2>
        <div className={gridClass}>
          <div>
            <label htmlFor="bs-vat_number" className={labelClass}>
              MwSt-Nummer
            </label>
            <input
              id="bs-vat_number"
              type="text"
              maxLength={50}
              value={form.vat_number || ""}
              onChange={setField(setForm, "vat_number")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div className="flex items-end pb-2">
            <label
              htmlFor="bs-is_vat_registered"
              className="inline-flex items-center gap-2 text-sm text-[#0f172a] dark:text-[#eef2ff]"
            >
              <input
                id="bs-is_vat_registered"
                type="checkbox"
                checked={Boolean(form.is_vat_registered)}
                onChange={setCheckbox(setForm, "is_vat_registered")}
                disabled={saving}
                className="h-4 w-4 rounded border-black/20 dark:border-white/20"
              />
              MwSt-registriert
            </label>
          </div>
          <div>
            <label htmlFor="bs-default_vat_rate" className={labelClass}>
              MwSt-Satz (%)
            </label>
            <input
              id="bs-default_vat_rate"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.default_vat_rate ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  default_vat_rate: v === "" ? null : Number(v),
                }));
              }}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-commercial_register_nr" className={labelClass}>
              Handelsregister-Nr.
            </label>
            <input
              id="bs-commercial_register_nr"
              type="text"
              maxLength={50}
              value={form.commercial_register_nr || ""}
              onChange={setField(setForm, "commercial_register_nr")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-legal_form" className={labelClass}>
              Rechtsform
            </label>
            <input
              id="bs-legal_form"
              type="text"
              maxLength={50}
              value={form.legal_form || ""}
              onChange={setField(setForm, "legal_form")}
              disabled={saving}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* ───────────── Section 3: Zahlung & QR-Bill ───────────── */}
      <section className={sectionClass}>
        <h2 className={sectionTitleClass}>Zahlung &amp; QR-Bill</h2>
        <div className={gridClass}>
          <div>
            <label htmlFor="bs-iban" className={labelClass}>
              IBAN
            </label>
            <input
              id="bs-iban"
              type="text"
              maxLength={34}
              value={form.iban || ""}
              onChange={setField(setForm, "iban")}
              disabled={saving}
              className={`${inputClass} uppercase`}
              style={{ textTransform: "uppercase" }}
            />
          </div>
          <div>
            <label htmlFor="bs-qr_iban" className={labelClass}>
              QR-IBAN
            </label>
            <input
              id="bs-qr_iban"
              type="text"
              maxLength={34}
              value={form.qr_iban || ""}
              onChange={setField(setForm, "qr_iban")}
              disabled={saving}
              className={`${inputClass} uppercase`}
              style={{ textTransform: "uppercase" }}
            />
          </div>
          <div>
            <label htmlFor="bs-reference_type" className={labelClass}>
              Referenz-Typ
            </label>
            <select
              id="bs-reference_type"
              value={form.reference_type || "SCOR"}
              onChange={setField(setForm, "reference_type")}
              disabled={saving}
              className={inputClass}
            >
              <option value="SCOR">SCOR (Strukturierte Referenz)</option>
              <option value="QRR">QRR (QR-Referenz)</option>
            </select>
          </div>
          <div>
            <label htmlFor="bs-bank_name" className={labelClass}>
              Bank
            </label>
            <input
              id="bs-bank_name"
              type="text"
              maxLength={100}
              value={form.bank_name || ""}
              onChange={setField(setForm, "bank_name")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-creditor_name" className={labelClass}>
              Kreditor (Name) *
            </label>
            <input
              id="bs-creditor_name"
              type="text"
              maxLength={200}
              value={form.creditor_name || ""}
              onChange={setField(setForm, "creditor_name")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-creditor_address_street" className={labelClass}>
              Kreditor — Strasse
            </label>
            <input
              id="bs-creditor_address_street"
              type="text"
              maxLength={200}
              value={form.creditor_address_street || ""}
              onChange={setField(setForm, "creditor_address_street")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-creditor_address_zip" className={labelClass}>
              Kreditor — PLZ
            </label>
            <input
              id="bs-creditor_address_zip"
              type="text"
              maxLength={20}
              value={form.creditor_address_zip || ""}
              onChange={setField(setForm, "creditor_address_zip")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-creditor_address_city" className={labelClass}>
              Kreditor — Ort
            </label>
            <input
              id="bs-creditor_address_city"
              type="text"
              maxLength={100}
              value={form.creditor_address_city || ""}
              onChange={setField(setForm, "creditor_address_city")}
              disabled={saving}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* ───────────── Section 4: Rechnungseinstellungen ───────────── */}
      <section className={sectionClass}>
        <h2 className={sectionTitleClass}>Rechnungseinstellungen</h2>
        <div className={gridClass}>
          <div>
            <label htmlFor="bs-default_language" className={labelClass}>
              Standardsprache
            </label>
            <select
              id="bs-default_language"
              value={form.default_language || "de"}
              onChange={setField(setForm, "default_language")}
              disabled={saving}
              className={inputClass}
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label htmlFor="bs-default_payment_terms_days" className={labelClass}>
              Zahlungsfrist (Tage)
            </label>
            <input
              id="bs-default_payment_terms_days"
              type="number"
              min="0"
              max="365"
              value={form.default_payment_terms_days ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  default_payment_terms_days: v === "" ? null : Number(v),
                }));
              }}
              disabled={saving}
              className={inputClass}
            />
            <p className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
              Wird als Standard für neue Rechnungen verwendet und kann
              pro Rechnung im Wizard überschrieben werden.
            </p>
          </div>
          <div>
            <label htmlFor="bs-invoice_prefix" className={labelClass}>
              Rechnungs-Präfix
            </label>
            <input
              id="bs-invoice_prefix"
              type="text"
              maxLength={20}
              value={form.invoice_prefix || ""}
              onChange={setField(setForm, "invoice_prefix")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-billing_day_of_month" className={labelClass}>
              Rechnungstag im Monat
            </label>
            <input
              id="bs-billing_day_of_month"
              type="number"
              min="1"
              max="31"
              value={form.billing_day_of_month ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  billing_day_of_month: v === "" ? null : Number(v),
                }));
              }}
              disabled={saving}
              className={inputClass}
            />
          </div>
          {/* Only the FOOTER is edited here (D4.1a). The two letterhead halves used
              to sit together, but they are not the same kind of setting: the footer
              is read by the Bexio invoice, the native invoice PDF, the handover
              protocol and the key receipt, so it belongs on this cross-document
              page. The header has exactly one consumer — the Bexio draft payload —
              and is therefore edited under Bexio › Einstellungen. It is still
              stored in this same row and still returned by this same endpoint;
              only the edit surface moved. Re-adding a header textarea here would
              recreate the second edit surface D4.1a removed. */}
          <div className="md:col-span-2">
            <label htmlFor="bs-default_footer_text" className={labelClass}>
              Standard-Fusszeile
            </label>
            <textarea
              id="bs-default_footer_text"
              rows={6}
              maxLength={5000}
              value={form.default_footer_text || ""}
              onChange={setField(setForm, "default_footer_text")}
              disabled={saving}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Standard-Fusszeile für Rechnungen und weitere Dokumente.
            </p>
          </div>
          <div>
            <label htmlFor="bs-logo_url" className={labelClass}>
              Logo-URL
            </label>
            <input
              id="bs-logo_url"
              type="url"
              maxLength={500}
              value={form.logo_url || ""}
              onChange={setField(setForm, "logo_url")}
              disabled={saving}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="bs-currency" className={labelClass}>
              Währung
            </label>
            <select
              id="bs-currency"
              value={form.currency || "CHF"}
              disabled
              title="Weitere Währungen folgen in einer späteren Version."
              className={inputClass}
            >
              <option value="CHF">CHF</option>
            </select>
          </div>
        </div>
      </section>

      {/* ───────────── Sticky save footer ───────────── */}
      <div className="sticky bottom-0 bg-white dark:bg-[#0c111b] border-t border-black/10 dark:border-white/[0.08] py-4 flex items-center justify-end gap-3 z-10">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="rounded-[8px] bg-[#0f172a] dark:bg-white text-white dark:text-[#0f172a] px-5 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Speichern…" : "Speichern"}
        </button>
      </div>
    </div>
  );
}
